import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import LendyIcon from "../../components/lendy/LendyIcon";
import AskAddyV2 from "../../models/askAddyV2";
import { AppLogo, AppRow } from "./AppRow";
import { AddMcpServerRow, McpNotice, McpServerRows, openSignIn, useMcpServers } from "./CustomMcpServers";
import { describeFeatured, FEATURED_APP_GROUPS, pickFeatured } from "./featuredApps";
import useAppCatalog from "./useAppCatalog";
import { TOAST_OPTIONS } from "./toastOptions";
import useCategoryApps from "./useCategoryApps";
import "../../components/lendy/lendyShared.css";
import "../profile/lendyProfile.css";
import "./appsPage.css";

// How many apps a section shows before "See all" opens the whole list.
const PREVIEW_COUNT = 4;
const MORE_ID = "more";
const MORE_LABEL = "All apps";
const ALL_FEATURED = FEATURED_APP_GROUPS.flatMap(({ apps }) => apps);
const TABS = [
  { id: "available", label: "Available" },
  { id: "connected", label: "Connected" },
];

const PageHead = ({ title, backLabel, onBack }) => (
  <div className="aav2-apps-head">
    {onBack && (
      <button type="button" className="lendy-float-button" aria-label={backLabel} title={backLabel} onClick={onBack}>
        <LendyIcon name="left" />
      </button>
    )}
    <h2>{title}</h2>
  </div>
);

/** A titled block of the page; "See all" opens the whole category. */
const Section = ({ title, seeAllLabel = `See all ${title}`, onSeeAll, children }) => (
  <div className="aav2-apps-section" role="group" aria-label={title}>
    <div className="aav2-apps-section-head">
      <h3>{title}</h3>
      {onSeeAll && (
        <button type="button" className="aav2-apps-see-all" aria-label={seeAllLabel} onClick={onSeeAll}>
          See all
          <LendyIcon name="right" size={14} />
        </button>
      )}
    </div>
    {children}
  </div>
);

/**
 * The apps and MCP servers the assistant can use, after the connectors page of Claude and ChatGPT:
 * a search, Available (Featured, your own MCP server, then the apps by category) and Connected (your
 * apps and MCP servers). Apps run through Composio: Connect opens the app's sign-in in a new tab and
 * the page refreshes when they come back. The chat can then use the app, and asks first before
 * anything destructive (see Approvals).
 */
const AppsContent = ({ identity, assistantName, landing, backLabel, onBack }) => {
  const [tab, setTab] = useState(landing ? "connected" : "available");
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState(null);
  const [connected, setConnected] = useState(null);
  const [popular, setPopular] = useState(null);
  const [isEnabled, setIsEnabled] = useState(true);
  const [busyApp, setBusyApp] = useState("");
  const [confirmingApp, setConfirmingApp] = useState("");
  const [error, setError] = useState("");
  const mcp = useMcpServers(identity);
  const { appsBySection, loadingIds } = useCategoryApps(identity, isEnabled);
  const scrollRef = useRef(null);

  const query = search.trim().toLowerCase();
  const isAvailable = tab === "available";
  const isMoreOpen = categoryId === MORE_ID;
  const { catalog, error: catalogError, endRef } = useAppCatalog(isAvailable ? search : "", identity, scrollRef, isAvailable && isEnabled && (Boolean(query) || isMoreOpen));

  // The apps connected at the last load: one that shows up since (signed in from another tab) gets a toast.
  const knownAppsRef = useRef(null);

  const loadConnected = useCallback(async () => {
    try {
      const page = await AskAddyV2.integrations.list({ connected: true }, identity);
      setIsEnabled(page.enabled);
      setConnected(page.apps);
      const known = knownAppsRef.current;
      if (known) {
        page.apps.filter(({ app }) => !known.has(app)).forEach(({ name }) => toast.success(`${name} is connected`, TOAST_OPTIONS));
      }
      knownAppsRef.current = new Set(page.apps.map(({ app }) => app));
    } catch (loadError) {
      setError(loadError.message);
    }
  }, [identity]);

  // The apps connected most: Featured leads with them. Without that data (the agent has no such route
  // yet, or Composio is down) the default picks stand in.
  useEffect(() => {
    let isCancelled = false;
    AskAddyV2.integrations
      .popular(identity)
      .then((page) => !isCancelled && setPopular(page?.apps || []))
      .catch(() => !isCancelled && setPopular([]));

    return () => {
      isCancelled = true;
    };
  }, [identity]);

  // Back from the app's sign-in tab: pick up the new connection.
  useEffect(() => {
    loadConnected();
    window.addEventListener("focus", loadConnected);

    return () => window.removeEventListener("focus", loadConnected);
  }, [loadConnected]);

  // Back from an app's sign-in: Connected, with a toast once the connections have loaded.
  const toastedLandingRef = useRef(null);
  useEffect(() => {
    if (!landing || connected === null || toastedLandingRef.current === landing.at) {
      return;
    }

    toastedLandingRef.current = landing.at;
    setTab("connected");
    setCategoryId(null);
    const name = [...connected, ...ALL_FEATURED].find(({ app }) => app === landing.app)?.name || "Your app";
    if (landing.status === "failed") {
      toast.error(`Couldn't connect ${name}. Try again from Available.`, TOAST_OPTIONS);

      return;
    }

    toast.success(`${name} is connected`, TOAST_OPTIONS);
  }, [landing, connected]);

  // Another tab, category or search starts at the top.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [tab, categoryId, search]);

  const connect = async (app) => {
    setBusyApp(app.app);
    setError("");
    try {
      await openSignIn(() => AskAddyV2.integrations.connect(app.app, identity));
    } catch (connectError) {
      setError(connectError.message);
    } finally {
      setBusyApp("");
    }
  };

  const disconnect = async (app) => {
    setBusyApp(app.app);
    setError("");
    try {
      await AskAddyV2.integrations.disconnect(app.app, identity);
      setConfirmingApp("");
      await loadConnected();
    } catch (disconnectError) {
      setError(disconnectError.message);
    } finally {
      setBusyApp("");
    }
  };

  const matchesSearch = ({ name, app }) => `${name} ${app}`.toLowerCase().includes(query);
  const connectedApps = new Set((connected || []).map(({ app }) => app));
  const isNotConnected = ({ app }) => !connectedApps.has(app);
  // Each section: our picks, then the rest of its Composio categories, most used first.
  const withCategoryApps = ({ id, apps }) => {
    const ours = new Set(apps.map(({ app }) => app));

    return [...apps, ...(appsBySection[id] || []).filter(({ app }) => !ours.has(app))].filter(isNotConnected);
  };
  const groups = FEATURED_APP_GROUPS.map((group) => ({ ...group, apps: withCategoryApps(group) })).filter(({ apps }) => apps.length);
  // All apps is the whole catalog: the ones in sections too, and the connected ones (shown as connected).
  const moreApps = catalog.apps;
  const connectedByApp = new Map((connected || []).map((app) => [app.app, app]));
  const shownError = error || catalogError;

  const renderAvailable = (app) => (
    <AppRow
      key={app.app}
      app={app}
      actions={
        <button
          type="button"
          className="lendy-btn lendy-btn--text aav2-apps-link"
          aria-label={`Connect ${app.name}`}
          disabled={busyApp === app.app}
          onClick={() => connect(app)}
        >
          {busyApp === app.app ? "Opening…" : "Connect"}
        </button>
      }
    />
  );

  const renderConnected = (app) => (
    <AppRow
      key={app.app}
      app={app}
      detail="Connected"
      actions={
        confirmingApp === app.app ? (
          <>
            <button type="button" className="lendy-pill-button lendy-pill-button--danger" disabled={busyApp === app.app} onClick={() => disconnect(app)}>
              Disconnect
            </button>
            <button type="button" className="lendy-btn lendy-btn--text" onClick={() => setConfirmingApp("")}>
              Keep
            </button>
          </>
        ) : (
          <button type="button" className="lendy-btn lendy-btn--text" aria-label={`Disconnect ${app.name}`} onClick={() => setConfirmingApp(app.app)}>
            Disconnect
          </button>
        )
      }
    />
  );

  const renderCatalogApp = (app) => (connectedByApp.has(app.app) ? renderConnected(connectedByApp.get(app.app)) : renderAvailable(app));

  const renderEnd = () => <div ref={endRef} className="aav2-apps-end" aria-hidden="true" />;

  const renderAvailableTab = () => {
    if (query) {
      const featuredMatches = FEATURED_APP_GROUPS.flatMap(({ apps }) => apps).filter((app) => matchesSearch(app) && isNotConnected(app));
      const listed = new Set(featuredMatches.map(({ app }) => app));
      const results = [...featuredMatches, ...catalog.apps.filter((app) => isNotConnected(app) && !listed.has(app.app))];

      return (
        <>
          {results.length > 0 && (
            <Section title="Results">
              <ul className="aav2-apps-list">{results.map(renderAvailable)}</ul>
            </Section>
          )}
          {catalog.isLoading && <p className="aav2-apps-note">{results.length ? "Loading more apps…" : "Loading…"}</p>}
          {!catalog.isLoading && !catalog.cursor && !results.length && <p className="aav2-apps-note">No apps match.</p>}
          {renderEnd()}
        </>
      );
    }

    if (categoryId) {
      const apps = isMoreOpen ? moreApps : groups.find(({ id }) => id === categoryId)?.apps || [];

      return (
        <>
          <ul className="aav2-apps-list">{apps.map(isMoreOpen ? renderCatalogApp : renderAvailable)}</ul>
          {((isMoreOpen && catalog.isLoading) || loadingIds.has(categoryId)) && <p className="aav2-apps-note">Loading more apps…</p>}
          {!apps.length && !catalog.isLoading && !loadingIds.has(categoryId) && <p className="aav2-apps-note">Nothing left to connect here.</p>}
          {renderEnd()}
        </>
      );
    }

    // Until the connected apps and the popular ones are known, the row keeps its place with empty cards,
    // so nothing below it moves when it fills in.
    const isFeaturedLoading = connected === null || popular === null;
    const spotlight = isFeaturedLoading ? [] : pickFeatured(popular, isNotConnected);

    return (
      <>
        {isEnabled && isFeaturedLoading && (
          <Section title="Featured">
            <div className="aav2-apps-featured" aria-busy="true">
              {[0, 1, 2].map((slot) => (
                <div key={slot} className="aav2-apps-card aav2-apps-card--loading" aria-hidden="true" />
              ))}
            </div>
          </Section>
        )}
        {isEnabled && spotlight.length > 0 && (
          <Section title="Featured">
            <div className="aav2-apps-featured">
              {spotlight.map((app) => (
                <button key={app.app} type="button" className="aav2-apps-card" aria-label={`Connect ${app.name}`} disabled={busyApp === app.app} onClick={() => connect(app)}>
                  <AppLogo app={app} />
                  <b>{app.name}</b>
                  <span>{busyApp === app.app ? "Opening…" : describeFeatured(app)}</span>
                </button>
              ))}
            </div>
          </Section>
        )}
        {mcp.isEnabled && (
          <Section title="MCP">
            <McpNotice mcp={mcp} />
            {!mcp.hasLoadFailed && (
              <ul className="aav2-apps-list">
                <AddMcpServerRow mcp={mcp} />
              </ul>
            )}
          </Section>
        )}
        {isEnabled &&
          groups.map(({ id, label, apps }) => (
            <Section key={id} title={label} onSeeAll={() => setCategoryId(id)}>
              <ul className="aav2-apps-list">{apps.slice(0, PREVIEW_COUNT).map(renderAvailable)}</ul>
            </Section>
          ))}
        {isEnabled && (moreApps.length > 0 || catalog.isLoading) && (
          <Section title={MORE_LABEL} seeAllLabel="See all apps" onSeeAll={() => setCategoryId(MORE_ID)}>
            <ul className="aav2-apps-list">{moreApps.slice(0, PREVIEW_COUNT).map(renderCatalogApp)}</ul>
            {catalog.isLoading && <p className="aav2-apps-note">Loading…</p>}
          </Section>
        )}
      </>
    );
  };

  const renderConnectedTab = () => {
    const shownConnected = (connected || []).filter(matchesSearch);
    const shownServers = (mcp.servers || []).filter(({ name }) => name.toLowerCase().includes(query));

    if (connected === null || mcp.servers === null) {
      return <p className="aav2-apps-note">Loading…</p>;
    }

    if (!shownConnected.length && !shownServers.length) {
      return (
        <>
          <McpNotice mcp={mcp} />
          <p className="aav2-apps-note">{query ? "No apps or MCP servers match." : "Nothing connected yet."}</p>
          {!query && (
            <button type="button" className="lendy-pill-button" onClick={() => setTab("available")}>
              Browse apps
            </button>
          )}
        </>
      );
    }

    return (
      <>
        <McpNotice mcp={mcp} />
        {shownConnected.length > 0 && (
          <Section title="Apps">
            <ul className="aav2-apps-list">{shownConnected.map(renderConnected)}</ul>
          </Section>
        )}
        {mcp.isEnabled && shownServers.length > 0 && (
          <Section title="MCP">
            <ul className="aav2-apps-list">
              <McpServerRows mcp={mcp} search={search} />
            </ul>
          </Section>
        )}
      </>
    );
  };

  const categoryLabel = isMoreOpen ? MORE_LABEL : FEATURED_APP_GROUPS.find(({ id }) => id === categoryId)?.label;
  const isCategoryOpen = Boolean(categoryId) && isAvailable && !query;

  return (
    <>
      {isCategoryOpen ? (
        <PageHead title={categoryLabel} backLabel="Back to Apps & MCP" onBack={() => setCategoryId(null)} />
      ) : (
        <PageHead title="Apps & MCP" backLabel={backLabel} onBack={onBack} />
      )}
      {!isCategoryOpen && (
        <div className="aav2-apps-controls">
          <label className="aav2-apps-search">
            <LendyIcon name="search" size={16} />
            <input
              type="search"
              aria-label="Search apps and MCP servers"
              placeholder="Search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setCategoryId(null);
              }}
            />
          </label>
          <div className="lendy-profile-tabs aav2-apps-tabs" role="tablist" aria-label="Apps & MCP">
            {TABS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                onClick={() => {
                  setTab(id);
                  setCategoryId(null);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
      <div ref={scrollRef} className="aav2-apps-scroll" role="tabpanel">
        <div className="aav2-apps-body">
          {!isCategoryOpen && !query && (
            <p className="aav2-apps-note">
              {isEnabled ? `Connect your apps and MCP servers so ${assistantName || "the assistant"} can use them in the chat.` : "Connected apps aren't set up on this server yet."}
            </p>
          )}
          {shownError && <p className="aav2-apps-note aav2-apps-note--error">{shownError}</p>}
          {isAvailable ? renderAvailableTab() : renderConnectedTab()}
        </div>
      </div>
    </>
  );
};

/** The Apps & MCP page, in the chat's place: opened from the rail (or the chats list on a narrow sidebar). */
const AppsPage = ({ organizationId, userId, loanId, assistantName, landing, backLabel, onBack }) => {
  const identity = useMemo(() => ({ organizationId, userId, loanId }), [organizationId, userId, loanId]);
  const isConnected = AskAddyV2.isEnabled() && Boolean(organizationId) && Boolean(userId);

  return (
    <section className="lendy-profile aav2-apps" aria-label="Apps & MCP">
      {isConnected ? (
        <AppsContent identity={identity} assistantName={assistantName} landing={landing} backLabel={backLabel} onBack={onBack} />
      ) : (
        <>
          <PageHead title="Apps & MCP" backLabel={backLabel} onBack={onBack} />
          <p className="aav2-apps-note aav2-apps-body">Apps aren't connected in this build.</p>
        </>
      )}
    </section>
  );
};

export default AppsPage;
